import * as React from 'react';
import classnames from 'classnames';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isNumber from 'lodash/isNumber';
import isPlainObject from 'lodash/isPlainObject';
import isString from 'lodash/isString';

import { Link } from 'libs/router';

import SystemMessageWrapperContext from '../wrapper/context/SystemMessageWrapperContext';

import { __t, applyTranslations } from 'utils/language';
import { sanitizeGenericHtml } from 'utils/sanitize';
import { getDisplayDate } from 'utils/date';
import { FUNC_RETURN_NULL } from '../utils/system-message';

import './system-message-ccm-campaign-participation.scss';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./system-message-ccm-campaign-participation.i18n.json'));

type Props = {
  type: 'simple' | 'verbose'
};

const COMMON_MSG_KEY = 'collabary_common';
const MSG_KEY = 'system_message_ccm_campaign_participation';

const SystemMessageCcmCampaignParticipation: React.FC<Props> = ({
  type
}): JSX.Element => {
  const context = React.useContext(SystemMessageWrapperContext);
  const {
    message,
    renderAvatar = FUNC_RETURN_NULL
  } = context;

  const metadata = message?.metadata ?? {};
  const campaign = metadata.campaign ?? {};
  const brand = metadata.brand ?? {};
  const creatorName = metadata.creatorName ?? 'Creator';
  const timeline = campaign.timeline ?? null;
  const hasGoLiveDate = isString(timeline?.taskContentGoLiveDate);
  const hasContentApproval = isString(timeline?.taskContentApprovalStartDate)
    && isString(timeline?.taskContentApprovalEndDate);

  const renderLink = (): React.ReactNode => {
    const text = __t(`${MSG_KEY}.links.view_brief`);

    const linkClasses = classnames('system-message-ccm-campaign-participation__link', {
      'system-message-ccm-campaign-participation__link--verbose': type === 'verbose'
    });

    return (
      <div className="system-message-ccm-campaign-participation__section">
        <Link
          to={`/campaigns/ccm/${campaign.id}/overview`}
          target="_blank"
          className={linkClasses}
          title={text}
        >
          {text}
        </Link>
      </div>
    );
  };

  const renderTitle = (): React.ReactNode => {
    const text = __t(`${MSG_KEY}.messages.system_message_${type}.title`, {
      creatorName,
      campaignName: campaign.name,
      brandName: brand.name
    });

    const titleClasses = classnames('system-message-ccm-campaign-participation__primary-text', {
      'system-message-ccm-campaign-participation__primary-text--verbose': type === 'verbose'
    });

    return (
      <div
        className={titleClasses}
        title={text}
      >
        {text}
      </div>
    );
  };

  const renderSubtitle = (): React.ReactNode => {
    const text = __t(`${MSG_KEY}.messages.system_message_${type}.subtitle`, {
      creatorName,
      campaignName: campaign.name,
      brandName: brand.name
    });

    const subtitleClasses = classnames('system-message-ccm-campaign-participation__additional-text', {
      'system-message-ccm-campaign-participation__additional-text--verbose': type === 'verbose'
    });

    return (
      <div
        className={subtitleClasses}
        title={text}
        dangerouslySetInnerHTML={{ __html: sanitizeGenericHtml(text) }}
      />
    );
  };

  const renderDescription = (): React.ReactNode => (
    <div className="system-message-ccm-campaign-participation__section">
      <div className="system-message-ccm-campaign-participation__header">
        {__t(`${MSG_KEY}.messages.system_message_${type}.labels.campaign_description`)}
      </div>
      <div className="system-message-ccm-campaign-participation__content">
        {campaign.description}
      </div>
    </div>
  );

  const renderTags = (): React.ReactNode => (
    <div className="system-message-ccm-campaign-participation__section">
      <div className="system-message-ccm-campaign-participation__header">
        {__t(`${MSG_KEY}.messages.system_message_${type}.labels.campaign_style`)}
      </div>
      <div className="system-message-ccm-campaign-participation__content">
        <div className="system-message-ccm-campaign-participation__tags">
          {
            campaign.tags.map(tag => (
              <div
                key={`system-message-ccm-campaign-participation__tag--${tag}`}
                className="system-message-ccm-campaign-participation__tag"
              >
                {__t(`${COMMON_MSG_KEY}.fashion_styles.${tag}`)}
              </div>
            ))
          }
        </div>
      </div>
    </div>
  );

  const renderCollectionLink = (): React.ReactNode => (
    <div className="system-message-ccm-campaign-participation__section">
      <div className="system-message-ccm-campaign-participation__header">
        {__t(`${MSG_KEY}.messages.system_message_${type}.labels.campaign_collection`)}
      </div>
      <div className="system-message-ccm-campaign-participation__content">
        <a
          href={campaign.collectionLink}
          rel="noopener noreferrer"
          target="_blank"
          className="system-message-ccm-campaign-participation__collection-link"
        >
          {__t(`${MSG_KEY}.links.view_collection`)}
        </a>
      </div>
    </div>
  );

  const renderExcludesAndOutfits = (): React.ReactNode => (
    <React.Fragment>
      {
        isArray(campaign.excludedBrands) && !isEmpty(campaign.excludedBrands) && (
          <div className="system-message-ccm-campaign-participation__section">
            <div className="system-message-ccm-campaign-participation__header">
              {__t(`${MSG_KEY}.messages.system_message_${type}.labels.exclude_brands`)}
            </div>
            <div className="system-message-ccm-campaign-participation__content">
              {
                campaign.excludedBrands
                  .map((name, index) => (
                    <span
                      key={`system-message-ccm-campaign-participation__excluded-brand--${name}-${index}`}
                    >
                      {name}
                    </span>
                  ))
                  .reduce((prev, curr) => [prev, ', ', curr])
              }
            </div>
          </div>
        )
      }
      {
        isNumber(campaign.numOutfits) && (
          <div className="system-message-ccm-campaign-participation__section">
            <div className="system-message-ccm-campaign-participation__header">
              {__t(`${MSG_KEY}.messages.system_message_${type}.labels.num_outfits`)}
            </div>
            <div className="system-message-ccm-campaign-participation__content">
              {campaign.numOutfits}
            </div>
          </div>
        )
      }
    </React.Fragment>
  );

  const renderTimeline = (): React.ReactNode => (
    <React.Fragment>
      {
        hasContentApproval && (
          <div className="system-message-ccm-campaign-participation__section">
            <div className="system-message-ccm-campaign-participation__header">
              {__t(`${MSG_KEY}.messages.system_message_${type}.labels.content_approval`)}
            </div>
            <div className="system-message-ccm-campaign-participation__content">
              {getDisplayDate(timeline.influencersSubmitOutfitsFrom)} - {getDisplayDate(timeline.influencersSubmitOutfitsUntil)}
            </div>
          </div>
        )
      }
      {
        hasGoLiveDate && (
          <div className="system-message-ccm-campaign-participation__section">
            <div className="system-message-ccm-campaign-participation__header">
              {__t(`${MSG_KEY}.messages.system_message_${type}.labels.go_live_date`)}
            </div>
            <div className="system-message-ccm-campaign-participation__content">
              {getDisplayDate(timeline.taskContentGoLiveDate)}
            </div>
          </div>
        )
      }
    </React.Fragment>
  );

  const renderSubsection = (
    renderSubsectionContent: () => React.ReactNode
  ): React.ReactNode => (
    <div className="system-message-ccm-campaign-participation__subsection">
      {renderSubsectionContent()}
    </div>
  );

  const containerClasses = classnames('system-message-ccm-campaign-participation', {
    'system-message-ccm-campaign-participation--verbose': type === 'verbose'
  });

  return (
    <React.Fragment>
      {
        type === 'simple' &&
          renderAvatar('handshake-inside-circle-custom-fg')
      }
      <div className={containerClasses}>
        {renderTitle()}
        {renderSubtitle()}
        {
          type === 'verbose' &&
            <div className="system-message-ccm-campaign-participation__contents">
              {
                isString(campaign.description)
                  && renderDescription()
              }
              {
                isArray(campaign.tags)
                  && renderTags()
              }
              {
                isString(campaign.collectionLink)
                  && renderCollectionLink()
              }
              {
                (isArray(campaign.excludedBrands) || isNumber(campaign.numOutfits))
                  && renderSubsection(renderExcludesAndOutfits)
              }
              {
                isPlainObject(timeline) && (hasGoLiveDate || hasContentApproval)
                  && renderSubsection(renderTimeline)
              }
              {renderLink()}
            </div>
        }
      </div>
    </React.Fragment>
  );
};

export default SystemMessageCcmCampaignParticipation;
